// Pure, DOM-free URL tokenizer for chat message text. Kept separate from the
// DOM glue (dom.ts) so the matching/trimming rules can be unit-tested without a
// browser. `buildMessage` renders these tokens as text nodes / <a> elements —
// never innerHTML — so message payloads carry no XSS surface.

// Matches http(s):// and bare www. URLs. Trailing punctuation is trimmed by the
// tokenizer so a link at the end of a sentence doesn't swallow the period/paren.
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const TRAILING_PUNCT_RE = /[.,!?;:)\]]+$/;

export interface LinkToken {
  type: "text" | "link";
  /** The visible text for this token (the URL as written, for links). */
  value: string;
  /** Present only for links: the resolved, navigable href. */
  href?: string;
}

/**
 * Split `text` into an ordered list of text/link tokens. Bare `www.` links get
 * an `https://` href so they're navigable while still displaying as typed.
 * A string with no URL yields a single text token (identical to the old
 * `textContent` behavior), so non-URL messages render exactly as before.
 */
export function tokenizeLinks(text: string): LinkToken[] {
  const tokens: LinkToken[] = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    let url = m[0];
    const trail = url.match(TRAILING_PUNCT_RE);
    if (trail) url = url.slice(0, url.length - trail[0].length);
    if (!url) continue;

    if (start > last)
      tokens.push({ type: "text", value: text.slice(last, start) });

    tokens.push({
      type: "link",
      value: url,
      href: url.startsWith("www.") ? `https://${url}` : url,
    });
    last = start + url.length;
  }
  if (last < text.length)
    tokens.push({ type: "text", value: text.slice(last) });
  return tokens;
}
